import React, { memo, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { PackageIcon, ClockIcon, CheckIcon, UserIcon, ThumbsUpIcon, ChevronRightIcon, getPlatformIcon } from './Icons';
import { getAdImageUrl } from '../../utils/adImageUrl';
import './ProductCard.css';

const ProductCard = memo(({
  product,
  isSelected = false,
  onSelect,
  showSelection = true,
  onPurchase
}) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { formatCurrency: formatCurrencyAmount, currentCurrency } = useCurrency();
  const {
    id,
    platform,
    type,
    features,
    seller,
    sellerAvatar,
    sellerInitial,
    price,
    currency: originalCurrency = 'EUR', // Original currency from ad
    rating,
    reviews,
    delivery,
    image,
    verified = true,
    currency = '€'
  } = product;

  const iconColors = useMemo(() => {
    const root = document.documentElement;
    return {
      secondary: getComputedStyle(root).getPropertyValue('--text-secondary').trim() || '#a0a0a0',
      tertiary: getComputedStyle(root).getPropertyValue('--text-tertiary').trim() || '#666666',
      accent: getComputedStyle(root).getPropertyValue('--accent-primary').trim() || '#ff6b35',
    };
  }, [theme]);

  const handleCardClick = (e) => {
    if (e.target.closest('.product-buy-button')) return;
    if (onSelect) onSelect(id);
    else navigate(`/ad/${id}`);
  };

  const platformDisplay = product.platform || '';
  const gameName = (product.game || product._adData?.game || '').toString().toLowerCase();
  const is8BallPool = gameName === '8-ball-pool' || gameName === '8 ball pool' || gameName.includes('8-ball') || gameName.includes('8ball');
  const is8BallPoolFormat = is8BallPool ||
    platformDisplay.startsWith('Cash:') || platformDisplay.startsWith('Level:') || platformDisplay.startsWith('Coins:') ||
    product.region?.startsWith('Level:') || product.status?.startsWith('Coins:');

  let cashValue = '', coinsValue = '', levelValue = '';
  if (is8BallPoolFormat) {
    if (platformDisplay.startsWith('Cash: ')) cashValue = platformDisplay.replace('Cash: ', '');
    if (product.status?.startsWith('Coins: ')) coinsValue = product.status.replace('Coins: ', '');
    if (product.region?.startsWith('Level: ')) levelValue = product.region.replace('Level: ', '');
  }

  const region = is8BallPoolFormat ? '' : (product.region || 'EU');
  const status = is8BallPoolFormat ? '' : (product.status || 'Ranked Ready');
  const platformValue = is8BallPoolFormat ? '' : (platformDisplay || 'PC');

  const images = product.images || product.image_urls || (image ? [image] : []);
  const rawPrimary = images[0] || image;
  const primaryImage = getAdImageUrl(rawPrimary) || rawPrimary;
  const [imageError, setImageError] = useState(false);
  const showPlaceholder = !primaryImage || imageError;
  const galleryCount = images.length;
  const hasMetadata = is8BallPoolFormat ? (cashValue || coinsValue) : (region || platformValue || status);

  // Header title: short label (type or first meaningful part)
  const headerTitle = type || (features && features.length > 40 ? features.slice(0, 40) + '…' : features) || 'Offer';

  // Use Link for instant navigation if onSelect is not provided
  const CardWrapper = onSelect ? 'div' : Link;
  const cardProps = onSelect 
    ? { 
        onClick: handleCardClick,
        style: { cursor: 'pointer' }
      }
    : { 
        to: `/ad/${id}`,
        onClick: handleCardClick,
        style: { textDecoration: 'none' }
      };

  return (
    <CardWrapper
      className={`product-item ${isSelected ? 'selected' : ''}`}
      {...cardProps}
    >
      {isSelected && showSelection && (
        <div className="selected-checkmark">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 5L7 12L4 9" stroke={iconColors.accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      )}

      {/* Header: badge + title + optional premium */}
      <div className="product-card-header">
        <div className="product-card-badge">
          {is8BallPoolFormat && (levelValue || cashValue || coinsValue) ? (
            <span className="product-card-badge-text">{levelValue || cashValue || coinsValue}</span>
          ) : (
            <span className="product-card-badge-text">{headerTitle}</span>
          )}
        </div>
        {product.boosted && (
          <span className="product-card-premium" aria-hidden>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
          </span>
        )}
      </div>

      {/* Description */}
      <p className="product-card-description">{features || '—'}</p>

      {/* Image with gallery overlay */}
      <div className="product-card-image-wrap">
        <div className="product-card-image">
          {showPlaceholder ? (
            <PackageIcon color={iconColors.tertiary} size={40} />
          ) : (
            <img
              src={primaryImage}
              alt={features || 'Ad'}
              loading="lazy"
              decoding="async"
              onError={() => setImageError(true)}
            />
          )}
        </div>
        {galleryCount > 1 && (
          <div className="product-card-gallery-overlay">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>
            <span>{galleryCount}+</span>
          </div>
        )}
      </div>

      {/* Stats bar */}
      {hasMetadata && (
        <div className="product-card-stats">
          {is8BallPoolFormat ? (
            <>
              {cashValue && (
                <span className="product-card-stat">
                  <img src="/accounts/8ballpool/cash.png" alt="" className="product-card-stat-icon-img" />
                  <span>{cashValue}</span>
                </span>
              )}
              {coinsValue && (
                <span className="product-card-stat">
                  <img src="/accounts/8ballpool/coins.png" alt="" className="product-card-stat-icon-img" />
                  <span>{coinsValue}</span>
                </span>
              )}
            </>
          ) : (
            <>
              {(region || platformValue) && (
                <span className="product-card-stat">
                  <span className="product-card-stat-icon">{getPlatformIcon(platformValue || region, iconColors.accent, 14)}</span>
                  <span>{platformValue || region}</span>
                </span>
              )}
              {(delivery || status) && (
                <span className="product-card-stat">
                  <ClockIcon color={iconColors.secondary} size={14} />
                  <span>{delivery || status}</span>
                </span>
              )}
            </>
          )}
        </div>
      )}

      {/* Price + Buy Now */}
      <div className="product-card-price-row">
        <span className="product-card-price">
          {currency}{typeof price === 'number' ? price.toFixed(2) : price}
        </span>
        <button
          type="button"
          className="product-buy-button"
          onClick={(e) => {
            e.stopPropagation();
            if (onPurchase) onPurchase(id);
            else if (onSelect) onSelect(id);
            else navigate(`/ad/${id}`);
          }}
        >
          <span>Buy Now</span>
          <ChevronRightIcon color="#ffffff" size={18} />
        </button>
      </div>

      {/* Seller row */}
      <div className="product-card-seller">
        <div className="product-card-seller-avatar">
          {sellerAvatar && !sellerAvatar.startsWith('letter:') ? (
            <img src={sellerAvatar} alt="" />
          ) : (
            <span>{sellerInitial || (seller && seller.charAt(0).toUpperCase()) || 'S'}</span>
          )}
        </div>
        <span className="product-card-seller-name">{seller || 'Seller'}</span>
        {verified && (
          <span className="product-card-seller-verified" aria-label="Verified">
            <CheckIcon color="#10b981" size={12} />
          </span>
        )}
        <span className="product-card-seller-sold">{reviews != null ? `${reviews} Sold` : ''}</span>
        <span className="product-card-seller-rating">
          <ThumbsUpIcon color="#4ade80" size={12} />
          {rating != null ? `${rating}%` : ''}
        </span>
      </div>
    </CardWrapper>
  );
});

ProductCard.displayName = 'ProductCard';

export default ProductCard;
